import '@fontsource/plus-jakarta-sans/400.css';
import '@fontsource/plus-jakarta-sans/500.css';
import '@fontsource/plus-jakarta-sans/600.css';

import {
  Box,
  Button,
  Container,
  CssBaseline,
  Stack,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import ErrorImg from '@shared/assets/images/backgrounds/errorimg.svg?url';
import { createAppTheme } from '@shared/theme/Theme';

const fallbackTheme = createAppTheme({ mode: 'light', direction: 'ltr' });

type ErrorBoundaryFallbackProps = {
  error: unknown;
  resetError: () => void;
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'Unknown error';
};

const ErrorBoundaryFallback = ({ error, resetError }: ErrorBoundaryFallbackProps) => {
  const { t } = useTranslation();

  const handleGoHome = () => {
    window.location.assign('/');
  };

  return (
    <ThemeProvider theme={fallbackTheme}>
      <CssBaseline />
      <Box
        display="flex"
        flexDirection="column"
        height="100vh"
        textAlign="center"
        justifyContent="center">
        <Container
          maxWidth="md"
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box component="img" src={ErrorImg} alt="" sx={{ width: '100%', maxWidth: 500, mb: 2 }} />
          <Typography align="center" variant="h1" mb={2}>
            {t('errorBoundary.title')}
          </Typography>
          <Typography
            align="center"
            variant="h5"
            color="text.secondary"
            mb={4}
            sx={{ maxWidth: 480 }}>
            {t('errorBoundary.description')}
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button color="primary" variant="contained" onClick={resetError} disableElevation>
              {t('errorBoundary.tryAgain')}
            </Button>
            <Button color="primary" variant="outlined" onClick={handleGoHome}>
              {t('errorBoundary.goHome')}
            </Button>
          </Stack>
          {import.meta.env.DEV && (
            <Box mt={4} sx={{ width: '100%', maxWidth: 480, textAlign: 'center' }}>
              <details>
                <summary style={{ cursor: 'pointer' }}>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {t('errorBoundary.errorDetails')}
                  </Typography>
                </summary>
                <Typography
                  component="pre"
                  variant="body2"
                  color="error"
                  mt={1}
                  align="center"
                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {getErrorMessage(error)}
                </Typography>
              </details>
            </Box>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default ErrorBoundaryFallback;
